(ns client-portal.client.voice-commerce.dashboard.views
  (:require [clojure.string :as s]
            [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.components.analytics :as cp.c]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.layout :as l]
            [client-portal.client.voice-commerce.charts.data :as vc.charts.d]
            [client-portal.client.voice-commerce.dashboard.data]
            [client-portal.client.voice-insights.charts.views :as vi.charts]
            [shared.core.components.components :as c]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.time :as time]
            [shared.core.util :as util]
            [shared.core.utils.currency :as currency]))

(defn- money [v]
  (str (currency/get-currency-symbol) (util/readable-number (or v 0))))

(defn- bucket-day [dt]
  (first (s/split (str dt) #"T")))

(defn- format-bucket
  "A chart bucket for display: `Sep 21` for a day and `11:00 PM` for an hour — or
   `Sep 21 11:00 PM` when the hours run across more than one day, where the time
   alone would repeat."
  [dt multi-day?]
  (let [dt (str dt)]
    (if (s/includes? dt "T")
      (time/reformat-date-str dt (if multi-day? "MMM d h:mm A" "h:mm A"))
      (time/reformat-date-str dt "MMM d"))))

(def ^:private stats
  "The dashboard's headline numbers, left to right, and how each renders."
  [{:key :orders :title "Orders" :icon-type :receipt-long :format util/readable-number}
   {:key :sales :title "Sales" :icon-type :payments :format money}
   {:key :avg-order-value :title "Avg Order Value" :icon-type :price-check :format money}
   {:key :total-customers :title "Total Customers" :icon-type :group :format util/readable-number}])

(defn dashboard-stats []
  (let [stats- (re/subscribe [:voice-commerce/dashboard-stats-])]
    (fn []
      (let [data @stats-]
        (if (or (util/loading? data) (nil? data))
          [c/loader-rectangle {:height 83
                               :style {:margin-top 24}}]
          (into [:div {:class-name style/grid-2-on-mobile-class-name
                       :style {:border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                               :border-radius "12px"
                               :display :flex
                               :margin-top 24}}]
                (mapv (fn [{:keys [key title icon-type format]}]
                        (let [{:keys [value over-time]} (get data key)]
                          [cp.c/header-with-content {:title title
                                                     :icon-type icon-type}
                           [:div {:style {:display :flex
                                          :gap 12}}
                            [:span {:style {:font-size 16
                                            :line-height "30px"
                                            :font-weight style/font-semi-bold-weight
                                            :color (style/alc-color (if (nil? value)
                                                                      "Text/secondary"
                                                                      "Text/primary"))}}
                             (if (nil? value)
                               "N/A"
                               (format value))]
                            ;; One point can't trend, so the sparkline waits for two.
                            (when (> (count over-time) 1)
                              [cp.c/header-line-chart
                               {:data (->> (sort-by :dt over-time)
                                           (map-indexed (fn [i {:keys [value]}]
                                                          {:x i
                                                           :y (or value 0)})))}])]]))
                      stats)))))))

(def orders-and-sales-chart-id "voice-commerce-orders-and-sales-by-date")

(defn orders-and-sales-chart
  "Orders and sales over the period. The two live on separate axes — a count and a
   dollar amount share no scale."
  []
  (let [chart-data- (re/subscribe [:voice-commerce/chart-data- orders-and-sales-chart-id])]
    (re/create-class
      {:component-did-mount #(re/dispatch [:voice-commerce/chart-initialize-listener
                                           orders-and-sales-chart-id
                                           false])
       :reagent-render
       (fn []
         (let [data @chart-data-
               loaded (util/when-not-loading data)
               ;; Zero-fill before anything reads the series, so the labels and the
               ;; lines see the same buckets.
               rows (when (sequential? loaded)
                      (vc.charts.d/fill-bucket-gaps loaded))
               multi-day? (-> (into #{} (map #(bucket-day (get % "dt"))) rows)
                              (count)
                              (> 1))
               format-dt #(format-bucket % multi-day?)]
           [vi.charts/chart-card
            {:chart-id orders-and-sales-chart-id
             :chart-type "LineChart"
             :title "Orders and Sales"
             :header-icon-type :shopping-cart
             :data (or rows data)
             :name-key "dt"
             :data-keys ["orders" "sales"]
             :right-y-keys ["sales"]
             ;; Sales is money on both the axis it owns and in the tooltip; orders is
             ;; a plain count, so the formatting is per series rather than chart-wide.
             :right-y-axis-config {:value-transf-fn (fn [{:keys [value]}] (money value))}
             :show-view-mode-toggle? false
             :use-animated-loading-block? true
             :config-overrides {:colors {"orders" (style/alc-color "Chart/tech_blue")
                                         "sales" (style/alc-color "Chart/orange")}
                                :y-axis-visible? true
                                :legend-label-fn util/->capitalize-words
                                ;; The buckets stay raw in the data so they sort; only
                                ;; the labels are formatted.
                                :x-axis-config {:value-transf-fn #(format-dt (:value %))}
                                :point-width 30
                                :tooltip-config {:header-transf-fn #(format-dt (:label %))
                                                 :label-transf-fn util/->capitalize-words
                                                 :row-fn (fn [{:keys [series-name] :as row}]
                                                           (cond-> row
                                                                   (= series-name "sales")
                                                                   (update :value #(str (currency/get-currency-symbol) %))))}}}]))})))

(defn concept-header
  "The concept's logo, name and location count, then the headline stats — the shape
   the Voice Insights concept dashboard uses."
  []
  (let [concept-id- (re/subscribe [:concepts/current-concept-id-])
        concepts-list- (re/subscribe [:concepts/list-data-])
        locations-by-concept-id- (re/subscribe [:locations/by-current-concept-id-])]
    (fn []
      (let [concept-label (if (util/loading? @concepts-list-)
                            :loading
                            (-> (concepts.d/concept-id+list->concept @concept-id- @concepts-list-)
                                (anon.d/concept-label)))
            locations @locations-by-concept-id-]
        [:div {:style {:display :flex
                       :flex-direction :column
                       :width "100%"
                       :padding 16}}
         [:div {:style {:display :flex
                        :align-items :center
                        :gap 12}}
          [:div {:style {:width 42
                         :height 42
                         :background-image (str "url(" (-> @concept-id- (concepts.d/concept-logo-url) (anon.d/logo-url)) ")")
                         :background-size :contain
                         :background-position :center
                         :background-repeat :no-repeat}}]
          [:div {:style {:display :flex
                         :flex-direction :column
                         :flex 1}}
           [:div {:style {:font-family style/roboto-font-family
                          :font-size 20
                          :line-height "32px"
                          :font-weight style/font-semi-bold-weight
                          :color (style/alc-color "Text/primary")}}
            (if (util/loading? concept-label)
              [c/loader-rectangle {:width 380
                                   :height 16
                                   :style {:margin "8px 0"}}]
              concept-label)]
           [:div {:style {:font-family style/roboto-font-family
                          :font-size 15
                          :font-weight style/font-medium-weight
                          :line-height :normal
                          :color (style/alc-color "Text/secondary")}}
            (if (util/loading? locations)
              [c/loader-rectangle {:width 240
                                   :height 14
                                   :style {:margin "2px 0"}}]
              (str (or (-> (count locations) (util/readable-number)) 0)
                   " "
                   (util/pluralize "Location" (count locations))))]]]
         [dashboard-stats]]))))

(defn location-header []
  (let [location-details- (re/subscribe [:locations/by-current-location-id-])]
    (fn []
      [:div {:style {:display :flex
                     :flex-direction :column
                     :gap 14
                     :width "100%"
                     :padding 16}}
       [cp.c/location-header {:location-details @location-details-}]
       [dashboard-stats]])))

(defn root [_]
  (let [location-id- (re/subscribe [:locations/current-selected-location-id-])]
    (re/create-class
      {:component-did-mount #(re/dispatch [:voice-commerce/dashboard-initialize])
       :reagent-render
       (fn [args]
         [l/logged-in-with-header-nav-voice-commerce
          (merge args {:concept-dropdown? true
                       :location-dropdown? true})
          ;; Same stats and charts either way — only the header above them changes.
          [:div {:style {:display :flex
                         :flex-direction :column
                         :width "100%"
                         :min-height "100%"}}
           (if @location-id-
             [location-header]
             [concept-header])
           ;; Same section spacing the Voice Insights dashboard puts below its header.
           [:div {:style {:width "100%"
                          :display :flex
                          :flex-direction :column
                          :gap 16
                          :padding 16}}
            [orders-and-sales-chart]]]])})))
